/**
 * Thin localStorage wrapper.
 *
 * Every service goes through here so that swapping the mock persistence for a
 * real API means replacing the service functions only — not hunting for
 * localStorage calls scattered across components.
 *
 * localStorage throws in private-browsing / blocked-cookie contexts, so all
 * access is guarded.
 */

export function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Writes a value, reporting whether it actually landed.
 *
 * Returns a boolean rather than throwing so no caller has to change, but the
 * answer matters: localStorage is about 5 MB for the whole origin, and a quota
 * failure used to be swallowed here. Anything that can push against that
 * ceiling — a background image, a long list of modules — looked saved until
 * the page was reloaded and it was gone. Callers that can hit the limit should
 * check the result and say so.
 */
export function writeJson<T>(key: string, value: T): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    const quotaExceeded =
      error instanceof DOMException &&
      (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED');
    console.error(
      quotaExceeded
        ? `[storage] "${key}" was not saved: browser storage is full.`
        : `[storage] "${key}" could not be saved.`,
      error,
    );
    return false;
  }
}

export function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function createId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
}
